<template>
  <div v-if="visible" class="mapPopup">
    <div class="mapPopupCont">
      <div class="mapPopupHeader">
        <span class="mapPopupTitle">{{ point.stationText }}</span>
        <div class="mapPopupClose" @click="close">×</div>
      </div>
      <div class="mapPopupBody">
        <div class="mapPopupTime">{{ formatDate(new Date(point.sTime), "yyyy-MM-dd HH:mm:ss") }}</div>
        <div class="mapPopupItems">
          <div 
            v-for="(it, i) in itemsOptions" 
            :key="i" 
            class="mapPopupItem"
            :class="{ active: activeItemId == it.itemId }"
            @click="selectItem(it)"
          >
            <div class="itemName">{{ it.itemName }}</div>
            <div class="itemValue">{{ str(it.selfStationItemValue) }}</div>
          </div>
        </div>
        <div class="mapPopupInfo">
          <div class="infoRow">
            <span class="infoLabel">水质类别：</span>
            <span class="infoValue">{{ str(point.waterCategory || '未知') }}</span>
          </div>
          <div class="infoRow">
            <span class="infoLabel">超标项：</span>
            <span class="infoValue overStandard">{{ str(point.overStandardItems || '无') }}</span>
          </div>
        </div>
        <div class="mapPopupChart" v-if="itemsOptions.length">
          <div class="chartTitle">趋势图</div>
          <div ref="chart" class="chartContainer"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import screen from "@/api/screen";

export default {
  name: 'MapPopup',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    point: {
      type: Object,
      default: null
    },
    itemsOptions: {
      type: Array,
      default: () => []
    },
    activeItemId: {
      type: String,
      default: ''
    },
    timeType: {
      type: String,
      default: 'hour'
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  watch: {
    activeItemId: {
      handler() {
        this.drawChart();
      },
      immediate: true
    },
    visible: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.drawChart();
          });
        }
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    selectItem(item) {
      this.$emit('select-item', item);
    },
    drawChart() {
      if (!this.$refs.chart || !this.point || !this.activeItemId) return;
      
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }
      
      this.chartInstance = this.$echarts.init(this.$refs.chart);
      
      screen
        .stationDetailDItemTimingDiagrams({
          startTime: this.formatDate(
            new Date(+new Date(this.point.sTime) - 1000 * 60 * 60 * 24),
            "yyyy/MM/dd hh:mm"
          ),
          endTime: this.formatDate(
            new Date(this.point.eTime),
            "yyyy/MM/dd hh:mm"
          ),
          selfStationId: this.point.stationId,
          upstreamStationId: "",
          downstreamStationId: "",
          timeType: this.timeType,
          ditemId: this.activeItemId,
        })
        .then((res) => {
          const data = [
            {
              name: "本站",
              type: "line",
              symbolSize: 4,
              symbol: "circle",
              lineStyle: {
                width: 2,
              },
              itemStyle: {
                color: "#409eff",
              },
              data: this.$config.getArrKeys(res.result, "selfStationValve").map(v => 
                v === "null" ? null : this.str(v)
              ),
            },
          ];
          const xAxis = this.$config.getArrKeys(res.result, "timePoint");
          
          const option = {
            grid: {
              left: "10%",
              right: "5%",
              top: "10%",
              bottom: "25%",
            },
            tooltip: {
              trigger: "axis",
            },
            xAxis: {
              type: "category",
              data: xAxis,
              axisLine: {
                lineStyle: {
                  color: "#666",
                },
              },
              axisLabel: {
                color: "#999",
                fontSize: 9,
                rotate: 30,
                interval: Math.floor(xAxis.length / 4),
              },
            },
            yAxis: {
              type: "value",
              axisLine: {
                lineStyle: {
                  color: "#666",
                },
              },
              axisLabel: {
                color: "#999",
                fontSize: 9,
              },
              splitLine: {
                lineStyle: {
                  color: "rgba(255,255,255,0.1)",
                },
              },
            },
            series: data,
          };
          
          this.chartInstance.setOption(option);
        });
    },
    formatDate(date, format) {
      return this.$formatDate(date, format);
    },
    str(value) {
      return this.$str(value);
    }
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }
};
</script>

<style lang='stylus' scoped>
.mapPopup {
  position: absolute;
  z-index: 1000;
  min-width: 320px;
  max-width: 360px;
  
  .mapPopupCont {
    background: rgba(0, 20, 40, 0.95);
    border: 1px solid rgba(64, 158, 255, 0.5);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    overflow: hidden;
  }
  
  .mapPopupHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    background: rgba(64, 158, 255, 0.2);
    border-bottom: 1px solid rgba(64, 158, 255, 0.3);
    
    .mapPopupTitle {
      font-size: 16px;
      font-weight: bold;
      color: #fff;
    }
    
    .mapPopupClose {
      font-size: 20px;
      color: #999;
      cursor: pointer;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: all 0.3s;
      
      &:hover {
        color: #fff;
        background: rgba(255, 77, 79, 0.5);
      }
    }
  }
  
  .mapPopupBody {
    padding: 15px;
    
    .mapPopupTime {
      font-size: 13px;
      color: #999;
      margin-bottom: 12px;
    }
    
    .mapPopupItems {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 15px;
      max-height: 120px;
      overflow-y: auto;
      
      .mapPopupItem {
        flex: 0 0 calc(25% - 6px);
        min-width: 60px;
        background: rgba(0, 0, 0, 0.3);
        border: 2px solid transparent;
        border-radius: 4px;
        padding: 8px 4px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s;
        
        &:hover {
          background: rgba(64, 158, 255, 0.2);
        }
        
        &.active {
          border-color: #409eff;
          background: rgba(64, 158, 255, 0.3);
        }
        
        .itemName {
          font-size: 12px;
          color: #ccc;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .itemValue {
          font-size: 16px;
          font-weight: bold;
          color: #409eff;
        }
      }
    }
    
    .mapPopupInfo {
      margin-bottom: 15px;
      padding: 10px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 4px;
      
      .infoRow {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .infoLabel {
          font-size: 13px;
          color: #999;
          min-width: 70px;
        }
        
        .infoValue {
          font-size: 13px;
          color: #fff;
          font-weight: bold;
          
          &.overStandard {
            color: #ff4d4f;
          }
        }
      }
    }
    
    .mapPopupChart {
      .chartTitle {
        font-size: 13px;
        color: #999;
        margin-bottom: 8px;
      }
      
      .chartContainer {
        width: 100%;
        height: 120px;
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;
      }
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/MapPopup.vue